import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  /**
   * Gets product page URL safely.
   *
   * IMPORTANT:
   * Do not directly require productCard.getProductCardLink()
   * because custom ProductCard implementations may not expose it.
   */
  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (
      this.closest('product-card')
    );

    const hotspotProduct =
      /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
        this.closest('product-hotspot-component')
      );

    let productLink = null;

    /*
     * Product card
     */
    if (productCard) {
      /*
       * Use public method only if it exists.
       */
      if (typeof productCard.getProductCardLink === 'function') {
        try {
          productLink = productCard.getProductCardLink();
        } catch (error) {
          console.warn('[quick-add] getProductCardLink failed:', error);
        }
      }

      /*
       * Fallback:
       * Find the actual product anchor inside product-card.
       */
      if (!productLink) {
        productLink = productCard.querySelector(
          'a[href*="/products/"]'
        );
      }
    }

    /*
     * Product hotspot fallback
     */
    if (!productLink && hotspotProduct) {
      if (typeof hotspotProduct.getHotspotProductLink === 'function') {
        try {
          productLink = hotspotProduct.getHotspotProductLink();
        } catch (error) {
          console.warn('[quick-add] getHotspotProductLink failed:', error);
        }
      }

      if (!productLink) {
        productLink = hotspotProduct.querySelector(
          'a[href*="/products/"]'
        );
      }
    }

    /*
     * Last fallback:
     * Find product link from quick-add parent.
     */
    if (!productLink) {
      productLink = this.closest('product-card')?.querySelector(
        'a[href*="/products/"]'
      );
    }

    if (!productLink) {
      console.warn('[quick-add] Product link not found');
      return '';
    }

    let href = '';

    if (typeof productLink === 'string') {
      href = productLink;
    } else if (productLink instanceof HTMLAnchorElement) {
      href = productLink.href;
    } else if (productLink?.href) {
      href = productLink.href;
    }

    if (!href) {
      console.warn('[quick-add] Product href is empty');
      return '';
    }

    try {
      const url = new URL(href, window.location.origin);

      /*
       * IMPORTANT:
       * If product-card already changed the URL to a selected
       * variant, preserve it.
       */
      if (url.searchParams.has('variant')) {
        return url.toString();
      }

      /*
       * Otherwise add currently selected variant.
       */
      const selectedVariantId = this.#getSelectedVariantId();

      if (selectedVariantId) {
        url.searchParams.set('variant', selectedVariantId);
      }

      return url.toString();
    } catch (error) {
      console.warn('[quick-add] Invalid product URL:', href, error);
      return href;
    }
  }

  /**
   * Gets currently selected variant ID from product card.
   *
   * Supports custom ProductCard implementations.
   */
  #getSelectedVariantId() {
    const productCard =
      /** @type {import('./product-card').ProductCard | null} */ (
        this.closest('product-card')
      );

    if (!productCard) return null;

    if (typeof productCard.getSelectedVariantId === 'function') {
      try {
        return productCard.getSelectedVariantId() || null;
      } catch (error) {
        console.warn(
          '[quick-add] getSelectedVariantId failed:',
          error
        );
      }
    }

    /*
     * Fallback:
     * Look for selected variant data on the product card.
     */
    const selectedInput = productCard.querySelector(
      'input[type="radio"][data-variant-id]:checked'
    );

    if (
      selectedInput instanceof HTMLInputElement &&
      selectedInput.dataset.variantId
    ) {
      return selectedInput.dataset.variantId;
    }

    /*
     * Another common fallback.
     */
    const selectedElement = productCard.querySelector(
      '[data-selected-variant-id]'
    );

    if (selectedElement instanceof HTMLElement) {
      return (
        selectedElement.dataset.selectedVariantId ||
        null
      );
    }

    return null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener(
      'change',
      this.#closeQuickAddModal
    );

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
      {
        signal: this.#cartUpdateAbortController.signal,
      }
    );

    document.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener(
      'change',
      this.#closeQuickAddModal
    );

    this.#abortController?.abort();

    this.#cartUpdateAbortController.abort();

    document.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelectUpdate
    );
  }

  /**
   * Updates quick-add button state when product variant is selected.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;

    if (
      event.target.closest('product-card') !==
      this.closest('product-card')
    ) {
      return;
    }

    const productOptionsCount =
      this.dataset.productOptionsCount;

    const quickAddButton =
      productOptionsCount === '1'
        ? 'add'
        : 'choose';

    this.setAttribute(
      'data-quick-add-button',
      quickAddButton
    );
  };

  /**
   * Clears cached content when cart is updated.
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders variant picker in quick-add modal.
   *
   * @param {Element} newHtml
   */
  #updateVariantPicker(newHtml) {
    const modalContent =
      document.getElementById('quick-add-modal-content');

    if (!modalContent) return;

    const variantPicker =
      /** @type {VariantPicker | null} */ (
        modalContent.querySelector('variant-picker')
      );

    if (!variantPicker) return;

    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click.
   *
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    /*
     * Get URL safely.
     */
    const currentUrl = this.productPageUrl;

    console.log(
      '[quick-add] Quick Add clicked'
    );

    console.log(
      '[quick-add] Product URL:',
      currentUrl
    );

    /*
     * If URL cannot be found, do not silently fail.
     */
    if (!currentUrl) {
      console.error(
        '[quick-add] Product URL not found'
      );

      /*
       * Still try opening the modal.
       */
      this.#openQuickAddModal();

      return;
    }

    try {
      /*
       * Check cache.
       */
      let productGrid =
        this.#cachedContent.get(currentUrl);

      /*
       * Fetch product page if not cached.
       */
      if (!productGrid) {
        const html =
          await this.fetchProductPage(currentUrl);

        if (html) {
          const gridElement =
            html.querySelector(
              '[data-product-grid-content]'
            );

          if (gridElement) {
            productGrid =
              /** @type {Element} */ (
                gridElement.cloneNode(true)
              );

            this.#cachedContent.set(
              currentUrl,
              productGrid
            );
          }
        }
      }

      /*
       * Update modal content.
       */
      if (productGrid) {
        const freshContent =
          /** @type {Element} */ (
            productGrid.cloneNode(true)
          );

        await this.updateQuickAddModal(
          freshContent
        );

        this.#updateVariantPicker(
          freshContent
        );
      }

      /*
       * OPEN POPUP
       */
      this.#openQuickAddModal();

    } catch (error) {
      /*
       * Never let Quick Add completely die because
       * product fetch / morph / variant code failed.
       */
      console.error(
        '[quick-add] Failed to open Quick Add:',
        error
      );

      /*
       * Try opening the modal anyway.
       */
      this.#openQuickAddModal();
    }
  };

  #resetScroll() {
    const dialogComponent =
      document.getElementById('quick-add-dialog');

    if (!(dialogComponent instanceof QuickAddDialog)) {
      return;
    }

    const productDetails =
      dialogComponent.querySelector(
        '.product-details'
      );

    const productMedia =
      dialogComponent.querySelector(
        '.product-information__media'
      );

    productDetails?.scrollTo({
      top: 0,
      behavior: 'instant',
    });

    productMedia?.scrollTo({
      top: 0,
      behavior: 'instant',
    });
  }

  /**
   * @param {QuickAddDialog} dialogComponent
   */
  #stayVisibleUntilDialogCloses(
    dialogComponent
  ) {
    this.toggleAttribute(
      'stay-visible',
      true
    );

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () =>
        this.toggleAttribute(
          'stay-visible',
          false
        ),
      {
        once: true,
      }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (!(dialogComponent instanceof QuickAddDialog)) {
      console.error(
        '[quick-add] #quick-add-dialog not found'
      );

      return;
    }

    this.#stayVisibleUntilDialogCloses(
      dialogComponent
    );

    dialogComponent.showDialog();

    const dialog =
      dialogComponent.refs?.dialog;

    if (!dialog) return;

    dialog.addEventListener(
      'animationstart',
      this.#resetScroll.bind(this),
      {
        once: true,
      }
    );
  };

  #closeQuickAddModal = () => {
    const dialogComponent =
      document.getElementById(
        'quick-add-dialog'
      );

    if (!(dialogComponent instanceof QuickAddDialog)) {
      return;
    }

    dialogComponent.closeDialog();
  };

  /**
   * Fetches product page content.
   *
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(
    productPageUrl
  ) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();

    this.#abortController =
      new AbortController();

    try {
      const response =
        await fetch(productPageUrl, {
          signal:
            this.#abortController.signal,
        });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch product page: HTTP error ${response.status}`
        );
      }

      const responseText =
        await response.text();

      const html =
        new DOMParser().parseFromString(
          responseText,
          'text/html'
        );

      return html;

    } catch (error) {
      if (error?.name === 'AbortError') {
        return null;
      }

      console.error(
        '[quick-add] Fetch error:',
        error
      );

      return null;

    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders variant picker.
   *
   * @param {Element} productGrid
   */
  async updateQuickAddModal(
    productGrid
  ) {
    const modalContent =
      document.getElementById(
        'quick-add-modal-content'
      );

    if (!productGrid || !modalContent) {
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails =
        productGrid.querySelector(
          '.product-details'
        );

      const productFormComponent =
        productGrid.querySelector(
          'product-form-component'
        );

      const variantPicker =
        productGrid.querySelector(
          'variant-picker'
        );

      const productPrice =
        productGrid.querySelector(
          'product-price'
        );

      const productTitle =
        document.createElement('a');

      productTitle.textContent =
        this.dataset.productTitle || '';

      productTitle.href =
        this.productPageUrl;

      const productHeader =
        document.createElement('div');

      productHeader.classList.add(
        'product-header'
      );

      productHeader.appendChild(
        productTitle
      );

      if (productPrice) {
        productHeader.appendChild(
          productPrice
        );
      }

      productGrid.appendChild(
        productHeader
      );

      if (variantPicker) {
        productGrid.appendChild(
          variantPicker
        );
      }

      if (productFormComponent) {
        productGrid.appendChild(
          productFormComponent
        );
      }

      productDetails?.remove();
    }

    const payload =
      productGrid.getAttribute(
        'view-event-payload'
      ) || '';

    modalContent.setAttribute(
      'view-event-payload',
      payload
    );

    morph(
      modalContent,
      productGrid
    );

    this.#syncVariantSelection(
      modalContent
    );
  }

  /**
   * Sync selected variant from product card to modal.
   *
   * @param {Element} modalContent
   */
  #syncVariantSelection(
    modalContent
  ) {
    const selectedVariantId =
      this.#getSelectedVariantId();

    if (!selectedVariantId) return;

    const modalInputs =
      modalContent.querySelectorAll(
        'input[type="radio"][data-variant-id]'
      );

    for (const input of modalInputs) {
      if (
        input instanceof HTMLInputElement &&
        input.dataset.variantId ===
          selectedVariantId &&
        !input.checked
      ) {
        input.checked = true;

        input.dispatchEvent(
          new Event('change', {
            bubbles: true,
          })
        );

        break;
      }
    }
  }
}

if (
  !customElements.get(
    'quick-add-component'
  )
) {
  customElements.define(
    'quick-add-component',
    QuickAddComponent
  );
}

class QuickAddDialog extends DialogComponent {
  #abortController =
    new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.handleCartUpdate,
      {
        signal:
          this.#abortController.signal,
      }
    );

    this.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.addEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();

    this.removeEventListener(
      DialogCloseEvent.eventName,
      this.#handleDialogClose
    );
  }

  /**
   * Closes dialog after successful cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        this.closeDialog();
      })
      .catch((error) => {
        if (
          error?.name !==
          'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        const anchorElement =
          /** @type {HTMLAnchorElement} */ (
            html.querySelector(
              '.view-product-title a'
            )
          );

        const viewMoreDetailsLink =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.view-product-title a'
            )
          );

        const mobileProductTitle =
          /** @type {HTMLAnchorElement} */ (
            this.querySelector(
              '.product-header a'
            )
          );

        if (!anchorElement) return;

        if (viewMoreDetailsLink) {
          viewMoreDetailsLink.href =
            anchorElement.href;
        }

        if (mobileProductTitle) {
          mobileProductTitle.href =
            anchorElement.href;
        }
      })
      .catch((error) => {
        if (
          error?.name !==
          'AbortError'
        ) {
          console.warn(
            '[quick-add] Event promise rejected:',
            error
          );
        }
      });
  };

  #handleDialogClose = () => {
    const iosVersion =
      getIOSVersion();

    if (
      !iosVersion ||
      iosVersion.major >= 17 ||
      (
        iosVersion.major === 16 &&
        iosVersion.minor >= 4
      )
    ) {
      return;
    }

    requestAnimationFrame(() => {
      const grid =
        document.querySelector(
          '#ResultsList [product-grid-view]'
        );

      if (grid) {
        const currentWidth =
          grid.getBoundingClientRect()
            .width;

        grid.style.width =
          `${currentWidth - 1}px`;

        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (
  !customElements.get(
    'quick-add-dialog'
  )
) {
  customElements.define(
    'quick-add-dialog',
    QuickAddDialog
  );
}